<script>
import {createManualStorageFees, getServiceWarehouse} from "@/api/order/firstOrder";

/**
 * 创建杂费 - 模态框内部组件
 *
 * 由 SimpleTable 通过 fields.js 的 modals.create 配置渲染，接收：
 *   params    打开时 setParams 注入的参数（只放业务数据）
 *   close     关闭模态框的方法
 *   setTitle  设置模态框标题的方法
 *   refresh   重新拉取表格数据的方法
 */
export default {
  name: "CreateFeeModal",
  props: {
    params: {
      type: Object,
      default: () => ({})
    },
    close: {
      type: Function,
      default: () => {}
    },
    setTitle: {
      type: Function,
      default: () => {}
    },
    refresh: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      form: {
        orderNumber: "",
        warehouseId: 0,
        period: 1,
        volume: 0.01,
        remarks: ""
      },
      formRules: {
        // 每个字段只用一条规则：required 负责标红星与必填语义，
        // validator 统一处理空值/格式/范围（空值提示与 required 一致），
        // trigger 同时监听 blur 与 change —— InputNumber 值变化派发的是 on-form-change，
        // 只写 blur 会导致输入后错误提示无法及时清除。
        orderNumber: [
          {
            required: true,
            validator: (...args) => this.validateOrderNumber(...args),
            trigger: ["blur", "change"]
          }
        ],
        period: [
          {
            required: true,
            validator: (...args) => this.validatePeriod(...args),
            trigger: ["blur", "change"]
          }
        ],
        volume: [
          {
            required: true,
            validator: (...args) => this.validateVolume(...args),
            trigger: ["blur", "change"]
          }
        ]
      },
      saving: false,
      serviceWarehouse: [],
    }
  },
  mounted() {
    // 需要时可动态修改标题
    // this.setTitle('创建杂费');
  },
  methods: {
    fetchServiceWarehouse() {
      this.$Spin.show()
      getServiceWarehouse({shipmentNumber: this.form.orderNumber}).then(res => {
        this.$Spin.hide()
        if (res.data.code) {
          return this.$Message.error(res.data.msg)
        }
        this.serviceWarehouse = res.data.payload
        this.$nextTick(() => {
          this.$forceUpdate()
        })
      })
    },
    // 判断是否为空（空串 / null / undefined / NaN 均视为空）
    isEmpty(value) {
      return (
        value === '' ||
        value === null ||
        value === undefined ||
        (typeof value === 'number' && isNaN(value))
      );
    },
    // 订单号：不能为空
    validateOrderNumber(rule, value, callback) {
      if (this.isEmpty(value)) {
        return callback(new Error("请输入订单号"));
      }
      callback();
    },
    // 收费天数：正整数，范围 1 ~ 9999
    validatePeriod(rule, value, callback) {
      if (this.isEmpty(value)) {
        return callback(new Error("请输入收费天数"));
      }
      const num = Number(value);
      if (!Number.isFinite(num)) {
        return callback(new Error("收费天数必须为数字"));
      }
      if (!Number.isInteger(num)) {
        return callback(new Error("收费天数必须为正整数"));
      }
      if (num < 1 || num > 9999) {
        return callback(new Error("收费天数需在 1 ~ 9999 之间"));
      }
      callback();
    },
    // 收费体积：浮点数，最小单位 0.01，范围 0.01 ~ 9999
    validateVolume(rule, value, callback) {
      if (this.isEmpty(value)) {
        return callback(new Error("请输入收费体积"));
      }
      const raw = String(value).trim();
      // 用字符串正则判断小数位，避免浮点误差误判（如 0.1 * 100）
      if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
        return callback(new Error("收费体积必须为数字，且最多保留两位小数"));
      }
      const num = Number(raw);
      if (num < 0.01 || num > 9999) {
        return callback(new Error("收费体积需在 0.01 ~ 9999 之间"));
      }
      callback();
    },
    handleSubmit() {
      // 归一化数值字段：空值保持空串，其余统一转成数字，避免字符串/异常值干扰校验
      ["period", "volume"].forEach(key => {
        const v = this.form[key];
        this.form[key] = this.isEmpty(v) ? "" : Number(v);
      });
      this.$refs.form.validate(valid => {
        if (!valid) return;
        this.saving = true;
        // 提交到后端接口
        createManualStorageFees(this.form).then(res => {
          this.saving = false;
          if (res.data.code) {
            return this.$Message.error(res.data.msg)
          } else {
            this.$Message.success("创建成功");
            // 刷新表格：refresh 由 SimpleTable 作为 prop 传入（与 close 同级）
            this.refresh();
            this.close();
          }
        })
      });
    },
    handleCancel() {
      this.close();
    }
  }
};
</script>

<template>
<div class="create-fee-modal">
  <Form ref="form" :model="form" :rules="formRules" :label-width="90">
    <FormItem label="订单号" prop="orderNumber">
      <Input v-model="form.orderNumber" placeholder="请输入订单号" @on-blur="fetchServiceWarehouse" />
    </FormItem>
    <FormItem label="服务仓库">
      <Select v-model="form.warehouseId" placeholder="请选择服务仓库" :disabled="serviceWarehouse.length === 0">
        <Option v-for="item in serviceWarehouse" :key="item.value" :label="item.label" :value="item.value" />
      </Select>
    </FormItem>
    <FormItem label="收费天数" prop="period">
      <!-- active-change=false：输入过程中不逐键格式化/夹取，失焦或回车时才应用，避免无法手动输入 -->
      <InputNumber
        v-model="form.period"
        :min="1"
        :max="9999"
        :precision="0"
        :active-change="false"
        style="width: 100%"
      />
    </FormItem>
    <FormItem label="收费体积" prop="volume">
      <!-- precision=2 时若逐键生效，输入 5 会立即变 5.00、输入前导 0 会被 min 夹成 0.01，
           导致无法正常手动输入；关闭 active-change 后在失焦/回车时才格式化 -->
      <InputNumber
        v-model="form.volume"
        :min="0.01"
        :max="9999"
        :step="0.01"
        :precision="2"
        :active-change="false"
        style="width: 100%"
      />
    </FormItem>
    <FormItem label="备注" prop="remarks">
      <Input v-model="form.remarks" type="textarea" :rows="3" placeholder="请输入备注" />
    </FormItem>
  </Form>
  <div class="modal-actions">
    <Button @click="handleCancel">取消</Button>
    <Button type="primary" :loading="saving" style="margin-left: 8px" @click="handleSubmit">确定</Button>
  </div>
</div>
</template>

<style scoped>
.create-fee-modal {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.modal-actions {
  margin-top: auto;
  padding-top: 12px;
  text-align: right;
  border-top: 1px solid #e8eaec;
}
</style>
